import { ReactNode, memo, ReactElement } from 'react'
import { Stack, Typography, ListItem, ListItemAvatar } from '@mui/material'
import { styled } from '@mui/material/styles'
import { TextWithSkeleton } from '$common/text-with-skeleton'
import { VerifiedAccount } from '$common/icons/verified-account'
import { useDisplayedConnectionStatus } from '$comp/main/react-hook'
import { AvatarBase } from '$common/avatar/avatar-base'
import { RemovePersonIcon } from '$common/icons/remove-person'
import { getAvatarText } from '$func/avatar-text'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { TextSlider } from '$common/text-slider'

const StyledListItem = styled(ListItem)`
  box-sizing: border-box;
  padding: 4px 8px;
  overflow: hidden;
  .MuiListItemAvatar-root {
    min-width: 40px;
  }
  cursor: pointer;
`
const InfoContainer = styled(Stack)`
  width: 100%;
  justify-content: center;
  margin-right: 8px;
  overflow: hidden;
`

const Container = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  overflow: hidden;
`

const StyledPrimaryText = styled(Typography)`
  margin-left: 4px;
`

interface Props {
  avatarSrc: string
  avatarBackgroundColor?: string
  deleted?: boolean
  primaryText: string
  onClick: () => unknown
  secondaryText?: string | ReactElement
  verified?: boolean
  callElement?: ReactElement | null
  avatarElement?: ReactNode
  backgroundColor?: string
}

function RoomAppBarInfoComponent({
  avatarSrc,
  avatarBackgroundColor = '',
  deleted = false,
  primaryText,
  secondaryText = '',
  verified = false,
  callElement = null,
  onClick,
  avatarElement,
  backgroundColor,
}: Props) {
  const connectionStatus = useDisplayedConnectionStatus()

  return (
    <Container>
      <StyledListItem onClick={onClick}>
        <ListItemAvatar>
          {avatarElement ? (
            avatarElement
          ) : (
            <AvatarBase
              src={avatarSrc}
              size={AvatarSize.SM}
              backgroundColor={avatarBackgroundColor}
              hasDeleted={deleted}
            >
              {deleted ? <RemovePersonIcon /> : getAvatarText(primaryText)}
            </AvatarBase>
          )}
        </ListItemAvatar>
        <InfoContainer>
          <Stack direction="row" alignItems="center">
            <TextWithSkeleton text={primaryText}>
              <TextSlider textColor="inherit">
                <>
                  <StyledPrimaryText variant="subtitle3" color="inherit">
                    {primaryText}
                  </StyledPrimaryText>
                  {verified && (
                    <VerifiedAccount
                      sx={{
                        color: 'primary500',
                        width: '17px',
                        height: '17px',
                      }}
                    />
                  )}
                </>
              </TextSlider>
            </TextWithSkeleton>
          </Stack>
          <StyledSecondaryText
            color={backgroundColor ? 'neutral000' : 'neutral600'}
            variant="caption1"
          >
            {connectionStatus || secondaryText}
          </StyledSecondaryText>
        </InfoContainer>
      </StyledListItem>

      {callElement}
    </Container>
  )
}

export const RoomAppBarInfo = memo(RoomAppBarInfoComponent)

const StyledSecondaryText = styled(Typography)`
  display: flex;
  gap: 4px;
`
